// useBasesAnalyze.js — Ai/Capture client hook for basesAnalyze

import { useCallback, useMemo, useState } from 'react';

import {
  addPendingAnalyze,
  countPendingAnalyzes,
  removePendingAnalyze,
} from 'spaces/hooks/basesAnalyzePending';
import { requestBasesAnalyze } from 'spaces/services/BasesAnalyzeService';
import { notifyBaseLimitError } from 'utilities/baseLimitFeedback';

export default function useBasesAnalyze({ baseKey, channelKey } = {}) {
  const [pendingByMessageKey, setPendingByMessageKey] = useState(() => new Map());
  const [analyzeError, setAnalyzeError] = useState('');

  const hasPendingAnalyze = pendingByMessageKey.size > 0;
  const pendingAnalyzeCount = pendingByMessageKey.size;

  const countPendingAnalyzesForContext = useCallback(({ threadRootKey } = {}) => (
    countPendingAnalyzes(pendingByMessageKey, { threadRootKey })
  ), [pendingByMessageKey]);

  const analyzeQuestion = useCallback(async ({
    userMessageKey,
    question,
    scopeHint,
    captureOrigin = false,
    threadRootKey = '',
    mentionKeys = [],
    selectedNodeKey = '',
    channelKey: channelKeyOverride = '',
  } = {}) => {
    const cleanBaseKey = String(baseKey || '').trim();
    const cleanChannelKey = String(channelKeyOverride || channelKey || '').trim();
    const cleanQuestion = String(question || '').trim();
    const cleanUserMessageKey = String(userMessageKey || '').trim();

    if (!cleanBaseKey || !cleanChannelKey || !cleanQuestion || !cleanUserMessageKey) {
      return null;
    }

    const cleanThreadRootKey = String(threadRootKey || '').trim();
    const cleanSelectedNodeKey = String(selectedNodeKey || '').trim();

    setAnalyzeError('');
    setPendingByMessageKey((previous) => addPendingAnalyze(
      previous,
      cleanUserMessageKey,
      cleanThreadRootKey,
    ));

    try {
      const result = await requestBasesAnalyze({
        baseKey: cleanBaseKey,
        channelKey: cleanChannelKey,
        userMessageKey: cleanUserMessageKey,
        question: cleanQuestion,
        scopeHint,
        captureOrigin: captureOrigin === true,
        threadRootKey: cleanThreadRootKey,
        mentionKeys,
        selectedNodeKey: cleanSelectedNodeKey,
      });

      if (result?.debug) {
        const logMethod = result.presentationKind === 'text_only' && result.rowCount === 0
          ? console.warn
          : console.info;
        logMethod('[Ai] basesAnalyze debug', result.debug);
      }

      return result;
    } catch (error) {
      console.error('[Ai] basesAnalyze failed', {
        message: error?.message || 'Unknown error',
        code: error?.code || null,
        details: error?.details || null,
      });
      const notified = notifyBaseLimitError({
        error,
        baseKey: cleanBaseKey,
        fallbackMessage: 'Ai could not answer that question.',
      });
      if (!notified) {
        setAnalyzeError(error?.message || 'Ai could not answer that question.');
      } else {
        setAnalyzeError('');
      }
      return null;
    } finally {
      setPendingByMessageKey((previous) => removePendingAnalyze(previous, cleanUserMessageKey));
    }
  }, [channelKey, baseKey]);

  const isAnalyzing = hasPendingAnalyze;

  return useMemo(() => ({
    analyzeQuestion,
    analyzeError,
    countPendingAnalyzes: countPendingAnalyzesForContext,
    hasPendingAnalyze,
    isAnalyzing,
    pendingAnalyzeCount,
  }), [
    analyzeError,
    analyzeQuestion,
    countPendingAnalyzesForContext,
    hasPendingAnalyze,
    isAnalyzing,
    pendingAnalyzeCount,
  ]);
}
